import { StatusOverview } from '@/components/StatusOverview';
import { ConfidenceSummary } from '@/components/ConfidenceSummary';
import { ServiceMonitor } from '@/components/ServiceMonitor';
import { CronHealthDashboard } from '@/components/CronHealthDashboard';
import { Activity, Loader2, AlertTriangle } from 'lucide-react';
import { useServices } from '@/hooks/useServices';
import { isSupabaseConfigured } from '@/integrations/supabase/client';
import elawayLogo from '@/assets/elaway-logo.png';

const Index = () => {
  const { services, loading, checking, checkAllServices, setServices, stats } = useServices();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <img src={elawayLogo} alt="Elaway Logo" className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Elaway Platform Monitor</h1>
                <p className="text-sm text-muted-foreground">
                  Real-time service health monitoring
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Activity className="h-4 w-4 text-accent animate-pulse" />
              <span>Live</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {!isSupabaseConfigured && (
          <div className="mb-8 p-4 rounded-lg border border-yellow-500/50 bg-yellow-500/10">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <div>
                <h3 className="font-semibold text-yellow-500">Supabase Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables to connect to your database.
                </p>
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading services from Excel...</p>
            </div>
          </div>
        ) : services.length === 0 ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-md">
              <img src={elawayLogo} alt="Elaway Logo" className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Services Found</h3>
              <p className="text-muted-foreground">
                Please ensure the Excel file is properly formatted with service information.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Overview Stats */}
            <section>
              <h2 className="text-xl font-semibold mb-4">System Overview</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <StatusOverview stats={stats} />
                </div>
                <div>
                  <ConfidenceSummary services={services} />
                </div>
              </div>
            </section>

            {/* Services Grid */}
            <section>
              <h2 className="text-xl font-semibold mb-4">Services</h2>
              <ServiceMonitor 
                services={services} 
                onServicesUpdate={setServices}
                onRefresh={checkAllServices}
                isChecking={checking}
              />
            </section>

            {/* Cron Job Health - At the bottom for advanced users */}
            <section className="mt-12">
              <CronHealthDashboard />
            </section>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16 py-6 min-h-[93px]">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Elaway Platform - Internal Service Monitoring System</p>
          <p className="mt-1">Auto-refresh every 5 minutes</p>
        </div>
      </footer>
    </div>
  );
};

export default Index;